import React, { useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  IconButton,
  CircularProgress
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useNavigate } from "react-router-dom";
import FooterCommonLogin from "./footerCommonLogin";
import { useDispatch } from "react-redux";
import {
  fetchNotifications,
  getPublicUrlThunk
} from "@modules/public-comments/redux/thunk/notificationsThunk";
import { useAppSelector, type AppDispatch } from "@store/store";
import type { NotificationsResponseItem } from "@modules/public-comments/services/apiEndpoints";

const border = "#E4E4E4";

const NotificationAll: React.FC<{
  notification: NotificationsResponseItem;
  index: number;
  onDownload: (doc?: any) => void;
}> = ({ notification, index, onDownload }) => {
  const navigate = useNavigate();

  const documentObj = notification.document;
  const displayDate = notification.created_at || notification.updated_at || "";

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${border}`,
        px: { xs: 0, md: 1 },
        py: 1.5,
        bgcolor: "#fff",
        "&:last-child": { borderBottom: "none" }
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Typography
          sx={{
            minWidth: 28,
            color: "#212121",
            fontWeight: 500,
            pt: "4px"
          }}
        >
          {index + 1}.
        </Typography>
        <Box flex={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.5px",
                color: "#212121",
                flex: 1
              }}
            >
              {notification.title}
            </Typography>
          </Stack>

          {documentObj && (
            <Stack
              onClick={() => onDownload(documentObj)}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <IconButton
                aria-label="Download Pdf"
                sx={{ width: 28, height: 28, color: "#E53935" }}
              >
                <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />
              </IconButton>

              <Typography
                sx={{ fontSize: 14, color: "#212121", fontWeight: 500, mr: 1, cursor: "pointer" }}
              >
                {documentObj.document_name?.split("-").pop() ?? ""}
              </Typography>

              <Typography sx={{ fontSize: 14, color: "#757575" }}>
                {displayDate ? new Date(displayDate).toLocaleDateString() : ""}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

const Notifications: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const items = useAppSelector(state => state.notifications?.items ?? []);
  const status = useAppSelector(state => state.notifications?.status ?? "idle");
  const error = useAppSelector(state => state.notifications?.error);

  useEffect(() => {
    // always fetch fresh on mount
    dispatch(fetchNotifications());
  }, [dispatch]);

  const handleDownload = useCallback(
    async (doc?: any) => {
      console.log("Download requested for document:", doc);
      if (!doc) return;
      try {
        console.log("in try");

        const payload: any = await dispatch(
          getPublicUrlThunk({
            key_name: doc.key_name,
            document_name: doc.document_name,
            moduletype: "row",
            folderName: "morth"
          }) as any
        ).unwrap();

        console.log("Received payload for download:", payload);

        if (!payload) return;

        // If API returned a public URL string, download/open it
        if (typeof payload === "string") {
          const a = document.createElement("a");
          a.href = payload;
          a.download = doc.document_name?.split("-").pop() || doc.key_name || "file";
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }

        // If payload is a Blob, trigger a download (no visible blob: URL)
        const blob: Blob = payload as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.document_name?.split("-").pop() || doc.key_name || "file";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed", err);
      }
    },
    [dispatch]
  );

  return (
    <>
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.10)), url(${require("@assets/images/common-login-bg.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: { xs: 120, md: 140 },
          display: "flex",
          alignItems: "center"
        }}
      >
        <Container
          maxWidth={false}
          sx={{ width: { xs: "95%", md: "95%" }, py: { xs: 4, md: 3 } }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#FFF",
              fontWeight: 800,
              fontSize: { xs: 24, md: 28 },
              lineHeight: 1.25
            }}
          >
            Notifications
          </Typography>
        </Container>
      </Box>

      <Box sx={{ bgcolor: "#fff" }}>
        <Container
          maxWidth={false}
          sx={{ width: { xs: "95%", md: "95%" }, py: { xs: 4, md: 3 } }}
        >
          <Paper
            elevation={0}
            sx={{ p: { xs: 2, md: 3 }, bgcolor: "transparent" }}
          >
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#212121" }}
                >
                  All Notifications
                </Typography>
              </Stack>

              <Paper elevation={0} sx={{ p: 0, mt: 1 }}>
                {status === "loading" && (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                )}

                {status === "failed" && (
                  <Box sx={{ p: 2 }}>
                    <Typography color="error">
                      Failed to load notifications.
                    </Typography>
                    {error && (
                      <Typography sx={{ mt: 1 }}>{String(error)}</Typography>
                    )}
                  </Box>
                )}

                {status !== "loading" && items.length === 0 && (
                  <Box sx={{ p: 2 }}>
                    <Typography>No notifications found.</Typography>
                  </Box>
                )}

                {items.map(
                  (notification: NotificationsResponseItem, idx: number) => (
                    <NotificationAll
                      key={notification.id ?? idx}
                      notification={notification}
                      index={idx}
                      onDownload={handleDownload}
                    />
                  )
                )}
              </Paper>
            </Stack>
          </Paper>
        </Container>
      </Box>
      <FooterCommonLogin />
    </>
  );
};

export default Notifications;
